"use client"

import { useNavigate } from "react-router-dom"
import axios from "axios"
import { UserContext } from "../context/UserContext.jsx"
import { useCallback, useContext, useEffect, useRef, useState, useMemo } from "react"
import { toast } from "sonner"

// Singleton to manage authentication state across the app
const AuthManager = (() => {
  let instance = null

  class AuthenticationManager {
    constructor() {
      this.isAuthenticated = false
      this.user = null
      this.isSubscribed = false
      this.listeners = new Set()
      this.hasCheckedInitialAuth = false
      this.authCheckPromise = null
    }

    // Register a listener for authentication changes
    subscribe(listener) {
      this.listeners.add(listener)
      return () => this.listeners.delete(listener)
    }

    // Notify all listeners of authentication state change
    notifyListeners() {
      this.listeners.forEach((listener) =>
        listener({
          isAuthenticated: this.isAuthenticated,
          user: this.user,
          isSubscribed: this.isSubscribed,
        }),
      )
    }

    // Check newsletter subscription status with proper error handling
    async checkSubscriptionStatus(api, userEmail) {
      if (!userEmail) return false
      
      try {
        const subscriptionResponse = await api.get(`/api/newsletter/status?email=${encodeURIComponent(userEmail)}`)
        return subscriptionResponse.data?.data?.isSubscribed || false
      } catch (error) {
        console.error("Newsletter subscription check failed:", error)
        return false
      }
    }

    // Perform authentication check only once or when explicitly requested
    async checkAuthentication(api, clearAuthData, saveUser, forceCheck = false) {
      // If a check is already in progress, return that promise
      if (this.authCheckPromise && !forceCheck) {
        return this.authCheckPromise
      }

      // If already checked and not forcing a re-check, return cached result
      if (this.hasCheckedInitialAuth && !forceCheck) {
        return this.isAuthenticated
      }

      // Create a new authentication check promise
      this.authCheckPromise = new Promise(async (resolve) => {
        try {
          const accessToken = localStorage.getItem("accessToken")
          const storedUser = localStorage.getItem("user")
          
          if (!accessToken || !storedUser) {
            clearAuthData()
            this.isAuthenticated = false
            this.user = null
            this.isSubscribed = false
            this.notifyListeners()
            this.hasCheckedInitialAuth = true
            resolve(false)
            return
          }

          try {
            const response = await api.get("/api/auth/verifyUser")

            if (response.status === 200 && response.data.user) {
              this.isAuthenticated = true
              this.user = response.data.user
              
              // Check newsletter subscription with the user's email
              this.isSubscribed = await this.checkSubscriptionStatus(api, response.data.user.email)
              
              saveUser(response.data.user)
              this.notifyListeners()
              resolve(true)
            } else {
              clearAuthData()
              this.isAuthenticated = false
              this.user = null
              this.isSubscribed = false
              this.notifyListeners()
              resolve(false)
            }
          } catch (error) {
            // Check if it's an unauthorized error (401)
            if (error.response && error.response.status === 401) {
              // Try to refresh the token silently
              try {
                const refreshToken = localStorage.getItem("refreshToken")
                if (refreshToken) {
                  const refreshResponse = await api.post("/api/auth/refresh", { refreshToken })
                  
                  if (refreshResponse.status === 200 && refreshResponse.data.accessToken) {
                    // Update token and retry verification
                    localStorage.setItem("accessToken", refreshResponse.data.accessToken)
                    const retryResponse = await api.get("/api/auth/verifyUser")
                    
                    if (retryResponse.status === 200 && retryResponse.data.user) {
                      this.isAuthenticated = true
                      this.user = retryResponse.data.user
                      this.isSubscribed = await this.checkSubscriptionStatus(api, retryResponse.data.user.email)
                      saveUser(retryResponse.data.user)
                      this.notifyListeners()
                      resolve(true)
                      return
                    }
                  }
                }
              } catch (refreshError) {
                console.error("Token refresh failed:", refreshError)
              }
            }
            
            clearAuthData()
            this.isAuthenticated = false
            this.user = null
            this.isSubscribed = false
            this.notifyListeners()
            resolve(false)
          }
          
          this.hasCheckedInitialAuth = true
        } catch (error) {
          console.error("Authentication check error:", error)
          clearAuthData()
          this.isAuthenticated = false
          this.user = null
          this.isSubscribed = false
          this.notifyListeners()
          this.hasCheckedInitialAuth = true
          resolve(false)
        } finally {
          this.authCheckPromise = null
        }
      });

      return this.authCheckPromise
    }

    // Logout method
    async logout(api, navigate, clearAuthData) {
      try {
        await api.post("/api/auth/logout")
      } catch (error) {
        console.error("Logout error:", error)
      } finally {
        clearAuthData()
        this.isAuthenticated = false
        this.user = null
        this.isSubscribed = false
        this.hasCheckedInitialAuth = false
        this.notifyListeners()
        navigate("/login")
      }
    }

    // Singleton instance creation
    static getInstance() {
      if (!instance) {
        instance = new AuthenticationManager()
      }
      return instance
    }
  }

  return AuthenticationManager
})()

const useAuth = () => {
  const navigate = useNavigate()
  const { saveUser, clearUser, user } = useContext(UserContext)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const authManagerRef = useRef(null)
  const hasPerformedInitialCheck = useRef(false)
  const refreshIntervalRef = useRef(null)

  const API_URL = import.meta.env.VITE_API_URL

  // Memoized Axios instance
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API_URL,
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
      timeout: 10000, // Add timeout to prevent hanging requests
    })

    // Request interceptor
    instance.interceptors.request.use(
      (config) => {
        const accessToken = localStorage.getItem("accessToken")
        const refreshToken = localStorage.getItem("refreshToken")

        if (accessToken) {
          config.headers["Authorization"] = `Bearer ${accessToken}`
        }

        if (refreshToken) {
          config.headers["X-Refresh-Token"] = refreshToken
        }

        return config
      },
      (error) => Promise.reject(error),
    )

    // Response interceptor to handle common errors
    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        // Don't show toast for auth verification endpoints
        const isAuthEndpoint = error.config.url.includes("/api/auth/verifyUser") || 
                             error.config.url.includes("/api/newsletter/status");
        
        if (!isAuthEndpoint && error.response) {
          // Show error toast for non-auth endpoints
          const errorMessage = error.response.data?.message || error.message
          toast.error(errorMessage)
        }
        return Promise.reject(error)
      }
    )

    return instance
  }, [API_URL])

  // Clear authentication data
  const clearAuthData = useCallback(() => {
    localStorage.removeItem("accessToken")
    localStorage.removeItem("refreshToken")
    localStorage.removeItem("user")
    clearUser()
    setIsAuthenticated(false)
    setIsSubscribed(false)
  }, [clearUser])

  // Logout functionality
  const logout = useCallback(async () => {
    const authManager = AuthManager.getInstance()
    await authManager.logout(api, navigate, clearAuthData)
  }, [navigate, api, clearAuthData])

  // Check authentication status
  const checkAuthStatus = useCallback(async (forceCheck = false) => {
    setIsLoading(true)

    // Get or create singleton auth manager
    if (!authManagerRef.current) {
      authManagerRef.current = AuthManager.getInstance()
    }

    try {
      // Check if we have user data in localStorage but not in context
      const storedUser = localStorage.getItem("user")
      if (storedUser && !user) {
        try {
          const parsedUser = JSON.parse(storedUser)
          saveUser(parsedUser)
          setIsAuthenticated(true)
        } catch (e) {
          console.error("Error parsing stored user:", e)
          localStorage.removeItem("user")
        }
      }

      const result = await authManagerRef.current.checkAuthentication(
        api, 
        clearAuthData, 
        saveUser,
        forceCheck
      )

      setIsAuthenticated(result)
      setIsSubscribed(authManagerRef.current.isSubscribed)
      hasPerformedInitialCheck.current = true
    } catch (error) {
      console.error("Auth check error:", error)
    } finally {
      setIsLoading(false)
    }
  }, [api, saveUser, clearAuthData, user])

  // Periodic silent verification to keep session alive
  useEffect(() => {
    if (isAuthenticated) {
      // Set up periodic check (every 15 minutes)
      refreshIntervalRef.current = setInterval(() => {
        // Silently verify auth status in the background
        checkAuthStatus(true).catch(console.error)
      }, 15 * 60 * 1000) // 15 minutes
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
      }
    }
  }, [isAuthenticated, checkAuthStatus])

  // Initial auth check
  useEffect(() => {
    // Only perform initial check once
    if (!hasPerformedInitialCheck.current) {
      checkAuthStatus()
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
      }
    }
  }, [checkAuthStatus])

  return {
    isAuthenticated,
    isLoading,
    user,
    logout,
    api,
    isSubscribed,
    checkAuthStatus, // Expose this to allow manual refresh
  }
}

export default useAuth